import { splitGraphemes, toNFC } from "./greekText"

export interface WeightedGreekWord {
  word: string
  weight: number
}

export interface GreekWordBanks {
  answers: string[]
  allowed: string[]
  answerWeights: WeightedGreekWord[]
}

const ANSWERS_PATH = "/wordlists/answers_gr.txt"
const ALLOWED_PATH = "/wordlists/allowed_gr.txt"
const ANSWERS_WEIGHTS_PATH = "/wordlists/answers_gr_weights.json"

async function loadWordList(path: string): Promise<string[]> {
  const response = await fetch(path)
  if (!response.ok) {
    throw new Error(`Failed to load ${path}`)
  }

  const content = await response.text()
  return content
    .split(/\r?\n/)
    .map((line) => toNFC(line.trim()))
    .filter((line) => line.length > 0)
}

async function loadWeightedWordList(path: string): Promise<WeightedGreekWord[]> {
  const response = await fetch(path)
  if (!response.ok) {
    throw new Error(`Failed to load ${path}`)
  }

  const parsed = (await response.json()) as WeightedGreekWord[]
  return parsed
    .map((entry) => ({
      word: toNFC(entry.word),
      weight: Number(entry.weight),
    }))
    .filter((entry) => entry.word.length > 0 && Number.isFinite(entry.weight))
}

export async function loadGreekWordBanks(): Promise<GreekWordBanks> {
  const [answers, allowed, answerWeights] = await Promise.all([
    loadWordList(ANSWERS_PATH),
    loadWordList(ALLOWED_PATH),
    loadWeightedWordList(ANSWERS_WEIGHTS_PATH),
  ])

  return {
    answers,
    allowed,
    answerWeights,
  }
}

export function filterWordsByLength(words: string[], wordLength: number): string[] {
  return words.filter((word) => splitGraphemes(word).length === wordLength)
}

export function filterWeightedWordsByLength(
  weightedWords: WeightedGreekWord[],
  wordLength: number
): WeightedGreekWord[] {
  return weightedWords.filter(
    (entry) => splitGraphemes(entry.word).length === wordLength
  )
}

function makeDaySeed(date: Date, wordLength: number): string {
  const yyyy = date.getFullYear().toString().padStart(4, "0")
  const mm = (date.getMonth() + 1).toString().padStart(2, "0")
  const dd = date.getDate().toString().padStart(2, "0")
  return `${yyyy}-${mm}-${dd}-${wordLength}`
}

function hashString(input: string): number {
  let hash = 2166136261

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return hash >>> 0
}

function seededRandom(seed: number): () => number {
  let state = seed

  return () => {
    state += 0x6d2b79f5
    let value = Math.imul(state ^ (state >>> 15), 1 | state)
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value)
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function weightedPick(
  weightedWords: WeightedGreekWord[],
  random: () => number
): string {
  const totalWeight = weightedWords.reduce(
    (sum, item) => sum + Math.max(0, item.weight),
    0
  )

  if (totalWeight <= 0) {
    const randomIndex = Math.floor(random() * weightedWords.length)
    return weightedWords[randomIndex].word
  }

  let roll = random() * totalWeight

  for (const item of weightedWords) {
    roll -= Math.max(0, item.weight)
    if (roll <= 0) {
      return item.word
    }
  }

  return weightedWords[weightedWords.length - 1].word
}

export function pickDailyWeightedWord(
  weightedWords: WeightedGreekWord[],
  wordLength: number,
  date: Date = new Date()
): string {
  const candidates = filterWeightedWordsByLength(weightedWords, wordLength)

  if (candidates.length === 0) {
    return ""
  }

  // Local date keeps the word changing at local midnight.
  const localDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const seed = hashString(makeDaySeed(localDay, wordLength))
  const random = seededRandom(seed)
  return weightedPick(candidates, random)
}
